/**
 * Returns the currency symbol for a given ISO 4217 currency code.
 * e.g. "USD" → "$", "EUR" → "€", "GBP" → "£", "INR" → "₹"
 */
export function getCurrencySymbol(currencyCode: string): string {
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? currencyCode;
  } catch {
    return currencyCode;
  }
}

/**
 * Formats an amount with the given ISO 4217 currency code.
 * e.g. formatCurrencyAmount(9.99, "USD") → "$9.99"
 */
export function formatCurrencyAmount(amount: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
    }).format(amount);
  } catch {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

// The platform currency (set by master admin), kept here so plain helpers —
// receipt printers, report formatters — can use it without React context.
// AuthProvider updates it whenever the platform currency loads/changes.
let activeCurrency = "USD";

export const setActiveCurrency = (currencyCode: string) => {
  activeCurrency = currencyCode || "USD";
};

/** ISO code of the platform currency, e.g. "INR". */
export const activeCurrencyCode = () => activeCurrency;

/** Symbol of the platform currency, e.g. "₹". */
export const activeCurrencySymbol = () => getCurrencySymbol(activeCurrency);

/** e.g. money(9.5) → "₹9.50" in the platform currency. */
export const money = (amount: number | string | null | undefined, digits = 2) =>
  `${activeCurrencySymbol()}${Number(amount || 0).toFixed(digits)}`;
